import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tooltip
} from '@mui/material';
import { Add, Delete, CheckCircle, HourglassEmpty, Star } from '@mui/icons-material';
import { collection, addDoc, updateDoc, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';

// Category definitions with emoji icons
const REWARD_CATEGORIES = [
  { value: 'screen_time', label: 'Screen Time', emoji: '📱', color: '#6C5CE7', bg: '#EEE8FF' },
  { value: 'treats',      label: 'Treats',      emoji: '🍦', color: '#E17055', bg: '#FFF0ED' },
  { value: 'activities',  label: 'Activities',  emoji: '🎮', color: '#00B894', bg: '#E8FFF8' },
  { value: 'toys',        label: 'Toys',        emoji: '🧸', color: '#FDCB6E', bg: '#FFFBEE' },
  { value: 'books',       label: 'Books',       emoji: '📚', color: '#0984E3', bg: '#EAF5FF' },
  { value: 'custom',      label: 'Custom',      emoji: '✨', color: '#A29BFE', bg: '#F5F4FF' },
];

function getCategoryInfo(categoryValue) {
  return REWARD_CATEGORIES.find(c => c.value === categoryValue) || REWARD_CATEGORIES[REWARD_CATEGORIES.length - 1];
}

// Reward Card for both parent and child views
function RewardCard({ reward, isParent, currentBalance, onDelete, onRedeem }) {
  const cat = getCategoryInfo(reward.category);
  const canAfford = !isParent && currentBalance >= reward.cost;
  const hasImage = reward.imageUrl && reward.imageUrl.trim() !== '';
  const displayIcon = reward.icon || cat.emoji;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        border: reward.featured ? `2px solid ${cat.color}` : '1px solid #e0e0e0',
        boxShadow: reward.featured
          ? `0 4px 20px ${cat.color}40`
          : '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 24px rgba(0,0,0,0.12)' }
      }}
    >
      {/* Featured badge */}
      {reward.featured && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            bgcolor: cat.color,
            color: '#fff',
            borderRadius: 2,
            px: 1,
            py: 0.25,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          <Star sx={{ fontSize: 12 }} />
          Featured
        </Box>
      )}

      {/* Image / Icon area */}
      <Box
        sx={{
          bgcolor: cat.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 120,
          overflow: 'hidden',
        }}
      >
        {hasImage ? (
          <img
            src={reward.imageUrl}
            alt={reward.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <Box
          sx={{
            display: hasImage ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <Typography sx={{ fontSize: '3rem', lineHeight: 1 }}>{displayIcon}</Typography>
        </Box>
      </Box>

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, pb: '12px !important' }}>
        {/* Category chip */}
        <Box sx={{ mb: 1 }}>
          <Chip
            label={`${cat.emoji} ${cat.label}`}
            size="small"
            sx={{
              bgcolor: cat.bg,
              color: cat.color,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>

        {/* Name */}
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ mb: 0.5, lineHeight: 1.3, flexGrow: 1 }}
        >
          {reward.name}
        </Typography>

        {/* Description */}
        {reward.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontSize: '0.78rem', lineHeight: 1.4 }}
          >
            {reward.description}
          </Typography>
        )}

        {/* Cost + actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: cat.color, fontSize: '1.1rem' }}
          >
            ${reward.cost.toFixed(2)}
          </Typography>

          {isParent ? (
            <Tooltip title="Remove reward">
              <IconButton
                onClick={() => onDelete(reward.id)}
                size="small"
                sx={{ color: 'error.light' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant={canAfford ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onRedeem(reward)}
              disabled={!canAfford}
              startIcon={canAfford ? <CheckCircle /> : <HourglassEmpty />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                ...(canAfford
                  ? { bgcolor: cat.color, '&:hover': { bgcolor: cat.color, filter: 'brightness(0.9)' } }
                  : {}),
              }}
            >
              {canAfford
                ? 'Redeem'
                : `+$${(reward.cost - currentBalance).toFixed(2)}`}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function RewardsStore({ currentUser, childProfile }) {
  useAuth();
  const { familyId } = useFamilyId();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newReward, setNewReward] = useState({
    name: '',
    cost: '',
    description: '',
    category: 'custom',
    imageUrl: '',
    icon: '',
    featured: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [confirmRedeem, setConfirmRedeem] = useState({ open: false, reward: null });

  const isParent = currentUser?.uid && !currentUser.uid.startsWith('child:');
  const isChild = currentUser?.uid?.startsWith('child:');

  // Load rewards and redemptions
  useEffect(() => {
    if (!familyId) return;

    const rewardsRef = collection(db, 'families', familyId, 'rewards');
    const rewardsQuery = query(rewardsRef, where('active', '==', true));

    const unsubscribeRewards = onSnapshot(rewardsQuery, (snapshot) => {
      const rewardsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRewards(rewardsData);
    });

    const redemptionsRef = collection(db, 'families', familyId, 'redemptions');
    const redemptionsQuery = query(redemptionsRef, where('status', 'in', ['pending', 'fulfilled']));

    const unsubscribeRedemptions = onSnapshot(redemptionsQuery, (snapshot) => {
      const redemptionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRedemptions(redemptionsData);
    });

    return () => {
      unsubscribeRewards();
      unsubscribeRedemptions();
    };
  }, [familyId]);

  const handleCreateReward = async () => {
    if (!newReward.name || !newReward.cost) {
      setError('Please fill in name and cost');
      return;
    }

    setCreating(true);
    setError('');

    try {
      await addDoc(collection(db, 'families', familyId, 'rewards'), {
        name: newReward.name.trim(),
        cost: parseFloat(newReward.cost),
        description: newReward.description.trim(),
        category: newReward.category || 'custom',
        imageUrl: newReward.imageUrl.trim(),
        icon: newReward.icon.trim(),
        featured: newReward.featured || false,
        active: true,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
      });

      setSuccess('Reward created!');
      setOpenCreateDialog(false);
      setNewReward({ name: '', cost: '', description: '', category: 'custom', imageUrl: '', icon: '', featured: false });

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating reward:', err);
      setError('Failed to create reward');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteReward = async (rewardId) => {
    try {
      await updateDoc(doc(db, 'families', familyId, 'rewards', rewardId), {
        active: false,
      });
    } catch (err) {
      console.error('Error deleting reward:', err);
    }
  };

  const handleRedeemReward = (reward) => {
    const currentBalance = childProfile?.allowance || 0;
    if (currentBalance < reward.cost) {
      setError(`Insufficient balance. You need $${(reward.cost - currentBalance).toFixed(2)} more.`);
      return;
    }
    setConfirmRedeem({ open: true, reward });
  };

  const executeRedeemReward = async (reward) => {
    const childId = currentUser.uid;
    const currentBalance = childProfile?.allowance || 0;
    try {
      await addDoc(collection(db, 'families', familyId, 'redemptions'), {
        rewardId: reward.id,
        rewardName: reward.name,
        rewardCost: reward.cost,
        childId: childId,
        childName: childProfile?.firstName || 'Child',
        status: 'pending',
        redeemedAt: new Date().toISOString(),
      });

      const childRef = doc(db, 'families', familyId, 'children', childProfile.childId);
      await updateDoc(childRef, {
        allowance: currentBalance - reward.cost,
      });

      setSuccess(`Redeemed ${reward.name}! Parent will fulfill it soon.`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error redeeming reward:', err);
      setError('Failed to redeem reward');
    }
  };

  const handleFulfillRedemption = async (redemption) => {
    try {
      await updateDoc(doc(db, 'families', familyId, 'redemptions', redemption.id), {
        status: 'fulfilled',
        fulfilledAt: new Date().toISOString(),
      });
      setSuccess('Reward fulfilled!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error fulfilling redemption:', err);
      setError('Failed to fulfill reward');
    }
  };

  // Filtered rewards based on category chip selection
  const filteredRewards = categoryFilter === 'all'
    ? rewards
    : rewards.filter(r => (r.category || 'custom') === categoryFilter);

  // Sorted: featured first
  const sortedRewards = [...filteredRewards].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  // Category filter chips — only show categories that exist in rewards
  const presentCategories = [...new Set(rewards.map(r => r.category || 'custom'))];

  const CategoryFilterChips = () => (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
      <Chip
        label="All"
        onClick={() => setCategoryFilter('all')}
        variant={categoryFilter === 'all' ? 'filled' : 'outlined'}
        color={categoryFilter === 'all' ? 'primary' : 'default'}
        sx={{ fontWeight: 600 }}
      />
      {REWARD_CATEGORIES.filter(c => presentCategories.includes(c.value)).map(cat => (
        <Chip
          key={cat.value}
          label={`${cat.emoji} ${cat.label}`}
          onClick={() => setCategoryFilter(cat.value)}
          variant={categoryFilter === cat.value ? 'filled' : 'outlined'}
          sx={{
            fontWeight: 600,
            ...(categoryFilter === cat.value
              ? { bgcolor: cat.color, color: '#fff', '&:hover': { bgcolor: cat.color, filter: 'brightness(0.9)' } }
              : { borderColor: cat.color, color: cat.color }),
          }}
        />
      ))}
    </Box>
  );

  // Create/Edit Reward Dialog
  const CreateRewardDialog = () => {
    const selectedCat = getCategoryInfo(newReward.category);
    return (
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span">{selectedCat.emoji}</Typography>
          Create New Reward
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Name */}
            <TextField
              label="Reward Name"
              placeholder="e.g., Extra Screen Time, Movie Night, Cash Out"
              value={newReward.name}
              onChange={(e) => setNewReward({ ...newReward, name: e.target.value })}
              fullWidth
              required
            />

            {/* Cost */}
            <TextField
              label="Cost ($)"
              type="number"
              placeholder="5.00"
              value={newReward.cost}
              onChange={(e) => setNewReward({ ...newReward, cost: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.01', min: '0' }}
            />

            {/* Category */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newReward.category}
                label="Category"
                onChange={(e) => setNewReward({ ...newReward, category: e.target.value })}
              >
                {REWARD_CATEGORIES.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{cat.emoji}</Typography>
                      <Typography>{cat.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              label="Description (optional)"
              placeholder="e.g., 30 minutes of extra screen time"
              value={newReward.description}
              onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            {/* Image URL */}
            <TextField
              label="Image URL (optional)"
              placeholder="https://example.com/image.png"
              value={newReward.imageUrl}
              onChange={(e) => setNewReward({ ...newReward, imageUrl: e.target.value })}
              fullWidth
              helperText="Paste a direct image URL, or leave blank to use the emoji icon below"
            />

            {/* Custom emoji icon */}
            <TextField
              label="Custom Emoji Icon (optional)"
              placeholder="e.g., 🎬 🍕 🚀"
              value={newReward.icon}
              onChange={(e) => setNewReward({ ...newReward, icon: e.target.value })}
              fullWidth
              helperText="Overrides the category emoji. One emoji recommended."
              inputProps={{ maxLength: 4 }}
            />

            {/* Featured toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                bgcolor: newReward.featured ? `${selectedCat.bg}` : 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: newReward.featured ? selectedCat.color : 'divider',
              }}
            >
              <Box>
                <Typography fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Star sx={{ fontSize: 18, color: newReward.featured ? selectedCat.color : 'text.disabled' }} />
                  Featured Reward
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Highlights this reward with a special badge at the top of the list
                </Typography>
              </Box>
              <Switch
                checked={newReward.featured}
                onChange={(e) => setNewReward({ ...newReward, featured: e.target.checked })}
                color="primary"
              />
            </Box>

            {/* Live preview chip */}
            {newReward.name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">Preview:</Typography>
                <Chip
                  label={`${newReward.icon || selectedCat.emoji} ${newReward.name}`}
                  size="small"
                  sx={{
                    bgcolor: selectedCat.bg,
                    color: selectedCat.color,
                    fontWeight: 600,
                  }}
                />
                {newReward.featured && (
                  <Chip
                    icon={<Star sx={{ fontSize: '14px !important' }} />}
                    label="Featured"
                    size="small"
                    sx={{ bgcolor: selectedCat.color, color: '#fff', fontWeight: 700 }}
                  />
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateReward}
            disabled={creating}
          >
            {creating ? <CircularProgress size={24} /> : 'Create Reward'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Parent View ────────────────────────────────────────────────────────────
  if (isParent) {
    const pendingRedemptions = redemptions.filter(r => r.status === 'pending');

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            🏆 Rewards Store
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenCreateDialog(true)}
          >
            Create Reward
          </Button>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Pending Redemptions */}
        {pendingRedemptions.length > 0 && (
          <Card sx={{ mb: 3, bgcolor: 'warning.light' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                ⏳ Pending Redemptions
              </Typography>
              {pendingRedemptions.map(redemption => (
                <Box
                  key={redemption.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2,
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography fontWeight="bold">{redemption.rewardName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {redemption.childName} • ${redemption.rewardCost.toFixed(2)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => handleFulfillRedemption(redemption)}
                  >
                    Mark Fulfilled
                  </Button>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Available Rewards */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Available Rewards
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {rewards.length} reward{rewards.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {rewards.length === 0 ? (
          <Alert severity="info">No rewards yet. Create some rewards for your kids!</Alert>
        ) : (
          <>
            <CategoryFilterChips />
            {sortedRewards.length === 0 ? (
              <Alert severity="info">No rewards in this category.</Alert>
            ) : (
              <Grid container spacing={2}>
                {sortedRewards.map(reward => (
                  <Grid item xs={12} sm={6} md={4} key={reward.id}>
                    <RewardCard
                      reward={reward}
                      isParent
                      onDelete={handleDeleteReward}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        <CreateRewardDialog />
      </Box>
    );
  }

  // ─── Child View ─────────────────────────────────────────────────────────────
  if (isChild) {
    const currentBalance = childProfile?.allowance || 0;
    const myRedemptions = redemptions.filter(r => r.childId === currentUser.uid);

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          🏆 Rewards Store
        </Typography>

        <Card sx={{ mb: 3, bgcolor: 'primary.light' }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold" color="primary.contrastText">
              ${currentBalance.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="primary.contrastText">
              Your Current Balance
            </Typography>
          </CardContent>
        </Card>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* My Redemptions */}
        {myRedemptions.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                My Redemptions
              </Typography>
              {myRedemptions.map(redemption => (
                <Box
                  key={redemption.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Box>
                    <Typography fontWeight="bold">{redemption.rewardName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ${redemption.rewardCost.toFixed(2)} • {new Date(redemption.redeemedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Chip
                    label={redemption.status === 'fulfilled' ? '✅ Fulfilled' : '⏳ Pending'}
                    color={redemption.status === 'fulfilled' ? 'success' : 'warning'}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Available Rewards */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Available Rewards
        </Typography>

        {rewards.length === 0 ? (
          <Alert severity="info">No rewards available yet. Ask your parents to add some!</Alert>
        ) : (
          <>
            <CategoryFilterChips />
            {sortedRewards.length === 0 ? (
              <Alert severity="info">No rewards in this category.</Alert>
            ) : (
              <Grid container spacing={2}>
                {sortedRewards.map(reward => (
                  <Grid item xs={12} sm={6} key={reward.id}>
                    <RewardCard
                      reward={reward}
                      isParent={false}
                      currentBalance={currentBalance}
                      onRedeem={handleRedeemReward}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Confirm Redeem Dialog */}
        <Dialog open={confirmRedeem.open} onClose={() => setConfirmRedeem({ open: false, reward: null })} maxWidth="xs" fullWidth>
          <DialogTitle>Redeem Reward?</DialogTitle>
          <DialogContent>
            <Typography>Redeem "{confirmRedeem.reward?.name}" for ${confirmRedeem.reward?.cost?.toFixed(2)}?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRedeem({ open: false, reward: null })} sx={{ minHeight: 48 }}>Cancel</Button>
            <Button variant="contained" color="primary" sx={{ minHeight: 48 }} onClick={() => {
              const { reward } = confirmRedeem;
              setConfirmRedeem({ open: false, reward: null });
              executeRedeemReward(reward);
            }}>Redeem</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return null;
}

export default RewardsStore;
